import React, { useState, useEffect, useRef } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import WorkflowManager from './WorkflowManager';
import RepositoryConnectModal from './RepositoryConnectModal';
import './WorkflowRunner.css';
import { executionService, workflowService, storageAdapter, isPlayground } from '../services';
import { useNotification } from '../contexts/NotificationContext';
import {
  Factory,
  Wrench,
  Rocket,
  AlertTriangle,
  Settings,
  TestTube,
  Link,
  Globe,
  FolderOpen,
  FileText,
  BarChart3,
  Clock,
  Clipboard,
  Search,
  AlertCircle,
  ArrowLeft,
  Tag,
  Trash2,
  History
} from 'lucide-react';

// Branch node types will be defined after component definitions

// Operation edge types
const operationTypes = {
  checkout: { label: 'checkout -b', color: '#007bff' },
  merge: { label: 'merge', color: '#28a745' },
  rebase: { label: 'rebase', color: '#ffc107' },
  push: { label: 'push', color: '#17a2b8' },
  pull: { label: 'pull', color: '#6f42c1' },
  'delete-branch': { label: 'delete', color: '#dc3545' },
  tag: { label: 'tag', color: '#fd7e14' },
};

function WorkflowRunner({ workflow, onBackToEditor, onWorkflowChange }) {
  const { showInfo: showInfoNotification } = useNotification();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionLog, setExecutionLog] = useState([]);
  const [showWorkflowSelector, setShowWorkflowSelector] = useState(false);
  const [repositoryPath, setRepositoryPath] = useState(isPlayground ? '/playground/demo-repo' : '');
  const [showRepositorySelector, setShowRepositorySelector] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [currentOperation, setCurrentOperation] = useState(null);
  const [currentExecutionId, setCurrentExecutionId] = useState(null);
  const [rightPanelTab, setRightPanelTab] = useState('logs');
  const [executionHistory, setExecutionHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedHistoryRecord, setSelectedHistoryRecord] = useState(null);
  const [historicalNodes, setHistoricalNodes] = useState([]);
  const [historicalEdges, setHistoricalEdges] = useState([]);

  // Ref to always hold the latest workflow without stale closures in socket handlers
  const workflowRef = useRef(workflow);
  workflowRef.current = workflow;


  // Refresh workflow data from storage when component mounts or workflow ID changes
  useEffect(() => {
    const refreshWorkflow = async () => {
      if (!workflow || !workflow.id) {
        return;
      }

      try {
        // Fetch the latest workflow data from storage
        const refreshedWorkflow = await workflowService.getWorkflow(workflow.id);
        if (refreshedWorkflow) {
          // Preserve the current repository path if it exists
          const currentRepoPath = repositoryPath;
          
          // Update the parent component's workflow state with the refreshed data
          if (onWorkflowChange) {
            onWorkflowChange(refreshedWorkflow);
          }
          
          // Restore repository path if it was set
          if (currentRepoPath) {
            setRepositoryPath(currentRepoPath);
          }
        }
      } catch (error) {
        console.error('Failed to refresh workflow data:', error);
        // Don't show error to user as this is a background refresh
      }
    };

    refreshWorkflow();
  }, [workflow?.id, onWorkflowChange]); // Only run when workflow ID changes

  // Socket.IO connection setup
  useEffect(() => {
    // Check if already connected to prevent duplicate connections
    const connectionStatus = executionService.getConnectionStatus();
    if (connectionStatus.isConnected) {
      return;
    }
    
    // Connect to Socket.IO server
    executionService.connect();
    
    // Define event handlers
    const handleExecutionStarted = (data) => {
      addLogEntry(`Execution started: ${data.workflowName}`, 'info');
    };

    const handleExecutionCompleted = (data) => {
      addLogEntry('Workflow execution completed successfully!', 'success');
      setIsExecuting(false);
      loadExecutionHistory();
    };

    const handleExecutionFailed = (data) => {
      addLogEntry(`Workflow execution failed: ${data.error}`, 'error');
      setIsExecuting(false);
      loadExecutionHistory();
    };

    const handleExecutionStopped = (data) => {
      // Use a more accurate message that doesn't claim the command was aborted
      const message = data.message;
      addLogEntry(message, 'warning');
      setIsExecuting(false);
      setCurrentExecutionId(null);
      loadExecutionHistory();
    };

    const handleOperationStarted = (data) => {
      addLogEntry(``, 'separator');
      addLogEntry(`Starting ${data.operationType} from ${data.source} to ${data.target}`, 'info');
      setCurrentOperation(data);
      
      // Update edge status to running
      console.log('Operation started:', data);
      setEdges(prev => {
        const updated = prev.map(edge => {
          // Match by operation ID instead of source/target
          const matches = edge.id === data.operationId;
          if (matches) {
            console.log('Found matching edge:', edge.id, 'updating to running');
            return { ...edge, data: { ...edge.data, status: 'running' } };
          }
          return edge;
        });
        console.log('Updated edges:', updated);
        return updated;
      });
      
      // Update node status for auto pull/push operations
      if (data.operationType === 'pull' || data.operationType === 'push') {
        setNodes(prev => prev.map(node => {
          if (node.data.name === data.target) {
            const updatedData = { ...node.data };
            if (data.operationType === 'pull') {
              updatedData.autoPullStatus = 'running';
            } else if (data.operationType === 'push') {
              updatedData.autoPushStatus = 'running';
            }
            return { ...node, data: updatedData };
          }
          return node;
        }));
      }
    };

    const handleOperationCompleted = (data) => {
      const message = `${data.operationType} completed successfully from ${data.source} to ${data.target}`;
      addLogEntry(message, 'success');
      setCurrentOperation(null);
      
      // Update edge status to success
      setEdges(prev => {
        const updated = prev.map(edge => {
          // Match by operation ID instead of source/target
          const matches = edge.id === data.operationId;
          if (matches) {
            return { ...edge, data: { ...edge.data, status: 'success' } };
          }
          return edge;
        });
        return updated;
      });
      
      // Update node status for auto pull/push operations
      if (data.operationType === 'pull' || data.operationType === 'push') {
        setNodes(prev => prev.map(node => {
          if (node.data.name === data.target) {
            const updatedData = { ...node.data };
            if (data.operationType === 'pull') {
              updatedData.autoPullStatus = 'success';
            } else if (data.operationType === 'push') {
              updatedData.autoPushStatus = 'success';
            }
            return { ...node, data: updatedData };
          }
          return node;
        }));
      }
    };

    const handleOperationFailed = (data) => {
      const message = `${data.operationType} failed from ${data.source} to ${data.target}: ${data.error}`;
      addLogEntry(message, 'error');
      setCurrentOperation(null);
      
      // Update edge status to failed
      setEdges(prev => {
        const updated = prev.map(edge => {
          // Match by operation ID instead of source/target
          const matches = edge.id === data.operationId;
          if (matches) {
            return { ...edge, data: { ...edge.data, status: 'failed' } };
          }
          return edge;
        });
        return updated;
      });
      
      // Update node status for auto pull/push operations
      if (data.operationType === 'pull' || data.operationType === 'push') {
        setNodes(prev => prev.map(node => {
          if (node.data.name === data.target) {
            const updatedData = { ...node.data };
            if (data.operationType === 'pull') {
              updatedData.autoPullStatus = 'failed';
            } else if (data.operationType === 'push') {
              updatedData.autoPushStatus = 'failed';
            }
            return { ...node, data: updatedData };
          }
          return node;
        }));
      }
    };

    const handleLogEntry = (data) => {
      addLogEntry(data.message, data.type, data.command);
    };

    const handleCommandBeforeExecution = (data) => {
      console.log('Received command-before-execution event:', data);
      addLogEntry(`Command: ${data.command}`, 'info', data.command);
    };

    // Set up real-time event listeners
    executionService.on('execution-started', handleExecutionStarted);
    executionService.on('execution-completed', handleExecutionCompleted);
    executionService.on('execution-failed', handleExecutionFailed);
    executionService.on('execution-aborted', handleExecutionStopped);
    executionService.on('operation-started', handleOperationStarted);
    executionService.on('operation-completed', handleOperationCompleted);
    executionService.on('operation-failed', handleOperationFailed);
    executionService.on('log-entry', handleLogEntry);
    executionService.on('command-before-execution', handleCommandBeforeExecution);

    // Cleanup on unmount - remove all event listeners
    return () => {
      executionService.off('execution-started', handleExecutionStarted);
      executionService.off('execution-completed', handleExecutionCompleted);
      executionService.off('execution-failed', handleExecutionFailed);
      executionService.off('execution-aborted', handleExecutionStopped);
      executionService.off('operation-started', handleOperationStarted);
      executionService.off('operation-completed', handleOperationCompleted);
      executionService.off('operation-failed', handleOperationFailed);
      executionService.off('log-entry', handleLogEntry);
      executionService.off('command-before-execution', handleCommandBeforeExecution);
      executionService.disconnect();
    };
  }, []);


  const handleWorkflowSelected = (selectedWorkflow) => {
    if (onWorkflowChange) {
      onWorkflowChange(selectedWorkflow);
    }
    setShowWorkflowSelector(false);
  };

  useEffect(() => {
    if (workflow) {
      // Set repository path from workflow
      setRepositoryPath(workflow.repositoryPath || (isPlayground ? '/playground/demo-repo' : ''));
      
      // Check for auto-pull operations to determine which branches should have autoPull enabled
      const autoPullBranches = new Set();
      const autoPullRemotes = new Map();
      workflow.operations
        .filter(operation => operation.id.startsWith('auto-pull-'))
        .forEach(operation => {
          // Extract branch ID from auto-pull operation ID (format: auto-pull-{branchId})
          const branchId = operation.id.replace('auto-pull-', '');
          autoPullBranches.add(branchId);
          // Extract remote name from operation parameters
          if (operation.params && operation.params.remote) {
            autoPullRemotes.set(branchId, operation.params.remote);
          }
        });

      // Check for auto-push operations to determine which branches should have autoPush enabled
      const autoPushBranches = new Set();
      const autoPushRemotes = new Map();
      workflow.operations
        .filter(operation => operation.id.startsWith('auto-push-'))
        .forEach(operation => {
          // Extract branch ID from auto-push operation ID (format: auto-push-{branchId})
          const branchId = operation.id.replace('auto-push-', '');
          autoPushBranches.add(branchId);
          // Extract remote name from operation parameters
          if (operation.params && operation.params.remote) {
            autoPushRemotes.set(branchId, operation.params.remote);
          }
        });

      // Check for auto-delete operations to restore deleteConfig to branches
      const branchDeleteConfigs = new Map();
      workflow.operations
        .filter(operation => operation.id.startsWith('auto-delete-'))
        .forEach(operation => {
          // Extract branch ID from auto-delete operation ID (format: auto-delete-{branchId})
          const branchId = operation.id.replace('auto-delete-', '');
          
          // Create deleteConfig object from operation params
          const deleteConfig = {
            enabled: true,
            remote: operation.params.remote || false,
            force: operation.params.force || false,
            remoteName: operation.params.remoteName || 'origin'
          };
          
          branchDeleteConfigs.set(branchId, deleteConfig);
        });
      
      // Convert workflow to React Flow format
      const flowNodes = workflow.branches.map((branch, index) => ({
        id: branch.id,
        type: branch.type,
        position: branch.position || { x: index * 250, y: 100 },
        data: {
          name: branch.name,
          branchName: branch.name,
          branchType: branch.type,
          isRemote: branch.isRemote || false,
          autoPull: branch.autoPull || autoPullBranches.has(branch.id),
          autoPullRemote: branch.autoPullRemote || autoPullRemotes.get(branch.id) || 'origin',
          autoPush: branch.autoPush || autoPushBranches.has(branch.id),
          autoPushRemote: branch.autoPushRemote || autoPushRemotes.get(branch.id) || 'origin',
          deleteConfig: branchDeleteConfigs.get(branch.id) || branch.deleteConfig || { enabled: false, remote: false, force: false, remoteName: 'origin' },
          tags: branch.tags || [],
          status: 'pending',
        },
      }));

      const flowEdges = workflow.operations
        .filter(operation => !operation.id.startsWith('auto-pull-') && !operation.id.startsWith('auto-push-') && !operation.id.startsWith('auto-tag-') && !operation.id.startsWith('auto-delete-')) // Filter out auto-pull, auto-push, auto-tag, and auto-delete operations
        .map((operation) => ({
          id: operation.id,
          source: operation.source,
          target: operation.target,
          type: 'operation',
          data: {
            operationType: operation.type,
            params: operation.params,
            status: 'pending',
          },
          label: operationTypes[operation.type]?.label || operation.type,
          labelStyle: { 
            fill: operationTypes[operation.type]?.color || '#333',
            fontWeight: 600 
          },
          labelBgStyle: { fill: 'white', fillOpacity: 0.8 },
        }));

      setNodes(flowNodes);
      setEdges(flowEdges);
    }
  }, [workflow, setNodes, setEdges]);

  const addLogEntry = (message, type = 'info', command = null) => {
    const timestamp = new Date().toLocaleTimeString();
    setExecutionLog(prev => [...prev, { timestamp, message, type, command }]);
  };

  // Auto-scroll to bottom when new logs are added
  useEffect(() => {
    const logContainer = document.querySelector('.execution-log');
    if (logContainer) {
      logContainer.scrollTop = logContainer.scrollHeight;
    }
  }, [executionLog]);


  // const updateBranchStatus = (branchId, status) => {
  //   setNodes(prev => prev.map(node => 
  //     node.id === branchId 
  //       ? { ...node, data: { ...node.data, status } }
  //       : node
  //   ));
  // };

  // const updateOperationStatus = (operationId, status) => {
  //   setEdges(prev => prev.map(edge => 
  //     edge.id === operationId 
  //       ? { ...edge, data: { ...edge.data, status } }
  //       : edge
  //   ));
  // };

  const executeWorkflow = async () => {
    if (isExecuting) return;
    
    // Validate repository path is required
    if (!repositoryPath || repositoryPath.trim() === '') {
      addLogEntry('ERROR: Repository path is required for security. Please select a Git repository folder.', 'error');
      return;
    }
    
    if (isPlayground) {
      showInfoNotification('Playground mode: Execution is simulated — no real git commands will run.', 4000);
    }

    setIsExecuting(true);
    setExecutionLog([]);
    addLogEntry(`Starting execution of workflow: ${workflow.name}`, 'info');
    addLogEntry(`Executing on repository: ${repositoryPath}`, 'info');

    // Reset all branches and operations to pending
    setNodes(prev => prev.map(node => ({
      ...node,
      data: { ...node.data, status: 'pending' }
    })));
    setEdges(prev => prev.map(edge => ({
      ...edge,
      data: { ...edge.data, status: 'pending' }
    })));

    try {
      // Start real execution
      const result = await executionService.startExecution(workflow.id, repositoryPath);
      setCurrentExecutionId(result.executionId);
      addLogEntry(`Execution started with ID: ${result.executionId}`, 'info');
      
      // Socket.IO events are already connected in useEffect above
      // Real-time updates will be handled automatically
      // Note: isExecuting will be set to false by handleExecutionCompleted or handleExecutionFailed
    } catch (error) {
      addLogEntry(`Workflow execution failed: ${error.message}`, 'error');
      setIsExecuting(false);
    }
  };

  const stopExecution = async () => {
    if (!currentExecutionId) return;
    
    try {
      addLogEntry('Stopping workflow execution...', 'info');
      await executionService.stopExecution(currentExecutionId);
      // The execution-stopped event will handle UI updates
    } catch (error) {
      addLogEntry(`Failed to stop execution: ${error.message}`, 'error');
    }
  };

  const previewWorkflow = async () => {
    if (isLoadingPreview) return;
    
    // Validate repository path is required
    if (!repositoryPath || repositoryPath.trim() === '') {
      addLogEntry('ERROR: Repository path is required for preview. Please select a Git repository folder.', 'error');
      return;
    }
    
    setIsLoadingPreview(true);
    setPreviewMode(true);
    setExecutionLog([]);
    addLogEntry(`Generating preview for workflow: ${workflow.name}`, 'info');
    addLogEntry(`Preview repository: ${repositoryPath}`, 'info');

    try {
      const preview = await executionService.previewWorkflow(workflow.id, repositoryPath);
      setPreviewData(preview);
      addLogEntry(`Preview generated successfully! ${preview.totalCommands} commands will be executed.`, 'success');
      addLogEntry(`Estimated duration: ${preview.estimatedDuration} seconds`, 'info');
    } catch (error) {
      addLogEntry(`Preview generation failed: ${error.message}`, 'error');
      setPreviewMode(false);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const exitPreviewMode = () => {
    setPreviewMode(false);
    setPreviewData(null);
    setExecutionLog([]);
  };

  const loadExecutionHistory = async () => {
    const wfId = workflowRef.current?.id;
    if (!wfId) return;
    setIsLoadingHistory(true);
    try {
      const history = await executionService.getExecutionHistory(wfId);
      setExecutionHistory(history || []);
    } catch (err) {
      console.error('Failed to load execution history:', err);
      setExecutionHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const loadHistoricalExecution = async (record) => {
    setSelectedHistoryRecord(record);
    try {
      const detail = await executionService.getExecutionDetail(record.id);
      setSelectedHistoryRecord(detail);

      // Reconstruct frozen nodes from saved branch states
      const frozenNodes = (detail.branches || []).map(branch => ({
        id: branch.id,
        type: branch.type,
        position: branch.position || { x: 0, y: 0 },
        data: {
          name: branch.name,
          branchType: branch.type,
          isRemote: branch.isRemote || false,
          autoPull: false,
          autoPush: false,
          deleteConfig: { enabled: false, remote: false, force: false, remoteName: 'origin' },
          tags: branch.tags || [],
          status: branch.status || 'pending',
        }
      }));

      // Reconstruct frozen edges from saved operation states (filter auto- ops)
      const frozenEdges = (detail.operations || [])
        .filter(op =>
          !op.id.startsWith('auto-pull-') &&
          !op.id.startsWith('auto-push-') &&
          !op.id.startsWith('auto-tag-') &&
          !op.id.startsWith('auto-delete-')
        )
        .map(op => ({
          id: op.id,
          source: op.source,
          target: op.target,
          type: 'operation',
          data: {
            operationType: op.type,
            params: op.params || {},
            status: op.status || 'pending',
          }
        }));

      setHistoricalNodes(frozenNodes);
      setHistoricalEdges(frozenEdges);
    } catch (err) {
      console.error('Failed to load historical execution:', err);
    }
  };

  // const simulateWorkflowExecution = async () => {
  //   // const operationMap = new Map(edges.map(edge => [edge.id, edge]));
  //   // const branchMap = new Map(nodes.map(node => [node.id, node]));
    
  //   // Build dependency map for operations
  //   const dependencies = new Map();
  //   edges.forEach(edge => {
  //     if (!dependencies.has(edge.id)) {
  //       dependencies.set(edge.id, []);
  //     }
  //     // Find operations that target the source of this operation
  //     edges.forEach(otherEdge => {
  //       if (otherEdge.target === edge.source && otherEdge.id !== edge.id) {
  //         dependencies.get(edge.id).push(otherEdge.id);
  //       }
  //     });
  //   });

  //   // Find operations with no dependencies (can run in parallel)
  //   const getReadyOperations = (completed) => {
  //     return edges.filter(edge => {
  //       const deps = dependencies.get(edge.id) || [];
  //       return deps.every(dep => completed.has(dep));
  //     });
  //   };

  //   const completed = new Set();
  //   const failed = new Set();

  //   while (completed.size + failed.size < edges.length) {
  //     const readyOperations = getReadyOperations(completed);
      
  //     if (readyOperations.length === 0) {
  //       // No more operations can be executed
  //       break;
  //     }

  //     // Execute ready operations in parallel
  //     const promises = readyOperations.map(async (edge) => {
  //       const operation = edge.data;
  //       updateOperationStatus(edge.id, 'running');
  //       addLogEntry(`Executing ${operation.operationType} from ${edge.source} to ${edge.target}`, 'info');

  //       try {
  //         // Simulate execution time
  //         await new Promise(resolve => setTimeout(resolve, 2000 + Math.random() * 3000));
          
  //         // Simulate success/failure (90% success rate for demo)
  //         const success = Math.random() > 0.1;
          
  //         if (success) {
  //           updateOperationStatus(edge.id, 'success');
  //           updateBranchStatus(edge.target, 'success');
  //           addLogEntry(`${operation.operationType} completed successfully from ${edge.source} to ${edge.target}`, 'success');
  //           completed.add(edge.id);
  //         } else {
  //           updateOperationStatus(edge.id, 'failed');
  //           updateBranchStatus(edge.target, 'failed');
  //           addLogEntry(`${operation.operationType} failed from ${edge.source} to ${edge.target}`, 'error');
  //           failed.add(edge.id);
  //         }
  //       } catch (error) {
  //         updateOperationStatus(edge.id, 'failed');
  //         updateBranchStatus(edge.target, 'failed');
  //         addLogEntry(`${operation.operationType} failed from ${edge.source} to ${edge.target}: ${error.message}`, 'error');
  //         failed.add(edge.id);
  //       }
  //     });

  //     await Promise.all(promises);
  //   }

  //   if (failed.size > 0) {
  //     throw new Error(`${failed.size} operations failed`);
  //   }
  // };

  const clearLogs = () => {
    setExecutionLog([]);
  };

  const exportLogs = () => {
    const logText = executionLog.map(entry => 
      `[${entry.timestamp}] ${entry.type.toUpperCase()}: ${entry.message}`
    ).join('\n');
    
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workflow-execution-${workflow.id}-${Date.now()}.log`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportPreviewCommands = () => {
    if (!previewData) return;
    
    const commandText = previewData.commands.map((cmd, index) => 
      `# ${index + 1}. ${cmd.description}\n${cmd.command}\n`
    ).join('\n');
    
    const header = `# Workflow Preview: ${previewData.workflowName}\n# Repository: ${previewData.repositoryPath}\n# Total Commands: ${previewData.totalCommands}\n# Estimated Duration: ${previewData.estimatedDuration} seconds\n\n`;
    
    const blob = new Blob([header + commandText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workflow-preview-${workflow.id}-${Date.now()}.sh`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };


  return (
    <div className="workflow-runner">
      <div className="runner-header">
        <div className="workflow-info">
          <button onClick={onBackToEditor} className="back-button">
            ← Back to Editor
          </button>
          <div className="workflow-title-section">
            <div className="workflow-details">
              <h2>{workflow?.name}</h2>
              <div className="workflow-id-row">
                <p>ID: {workflow?.id}</p>
                <button 
                  onClick={() => setShowWorkflowSelector(true)} 
                  className="load-workflow-button"
                >
                  <FolderOpen size={16} />
                  Browse
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="runner-controls">
          
          <div className="repository-section">
            <div className="repository-info">
              <span className="repository-label">Git Repository: <span className="required-indicator">*</span></span>
              <div>
                <span className="repository-path">{repositoryPath || 'NOT SELECTED'}</span>
                <button
                  onClick={() => {
                    if (isPlayground) {
                      showInfoNotification('Repository selection is not available in playground mode. Install GitPilot to connect real repositories.', 4000);
                      return;
                    }
                    setShowRepositorySelector(true);
                  }}
                  className="change-repository-button"
                >
                  Change
                </button>
              </div>
            </div>
          </div>
          
          {!previewMode ? (
            <div className="action-buttons">
              <button 
                onClick={previewWorkflow} 
                disabled={isLoadingPreview || !repositoryPath || repositoryPath.trim() === ''}
                className="preview-button"
              >
                {isLoadingPreview ? 'Generating Preview...' : 'Preview Workflow'}
              </button>
              {!isExecuting ? (
                <button 
                  onClick={executeWorkflow} 
                  disabled={!repositoryPath || repositoryPath.trim() === ''}
                  className="execute-button"
                >
                  Execute Workflow
                </button>
              ) : (
                <button 
                  onClick={stopExecution} 
                  className="stop-button"
                >
                  Abort Execution
                </button>
              )}
            </div>
          ) : (
            <div className="action-buttons">
              <button 
                onClick={exitPreviewMode} 
                className="exit-preview-button"
              >
                Exit Preview
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="runner-content">
        <div className="workflow-visualization">
          <ReactFlowProvider>
            <ReactFlow
              nodes={selectedHistoryRecord ? historicalNodes : nodes}
              edges={selectedHistoryRecord ? historicalEdges : edges}
              onNodesChange={selectedHistoryRecord ? undefined : onNodesChange}
              onEdgesChange={selectedHistoryRecord ? undefined : onEdgesChange}
              nodeTypes={branchNodeTypes}
              edgeTypes={{
                operation: OperationEdge,
              }}
              nodesDraggable={!selectedHistoryRecord}
              nodesConnectable={!selectedHistoryRecord}
              fitView
            >
              <Controls />
              <MiniMap />
              <Background variant="dots" gap={12} size={1} />
              <ArrowMarkers />
            </ReactFlow>
          </ReactFlowProvider>
        </div>

        {previewMode ? (
          <div className="preview-panel">
            <div className="panel-header">
              <h3>Workflow Preview</h3>
              <div className="preview-controls">
                <button onClick={exportPreviewCommands} className="export-button">
                  <FileText size={16} />
                  Export Script
                </button>
              </div>
            </div>
            
            {previewData ? (
              <div className="preview-content">
                <div className="preview-summary">
                  <div className="summary-grid">
                    <div className="summary-item">
                      <div className="summary-label">
                        <BarChart3 size={16} />
                        Total Commands
                      </div>
                      <div className="summary-value">{previewData.totalCommands}</div>
                    </div>
                    <div className="summary-item">
                      <div className="summary-label">
                        <Clock size={16} />
                        Estimated Duration
                      </div>
                      <div className="summary-value">{previewData.estimatedDuration}s</div>
                    </div>
                    <div className="summary-item repository-item">
                      <div className="summary-label">
                        <FolderOpen size={16} />
                        Repository Path
                      </div>
                      <div className="summary-value repository-value" title={previewData.repositoryPath}>
                        {previewData.repositoryPath}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="command-list">
                  <div className="command-list-header">
                    <h4>Commands (in execution order)</h4>
                  </div>
                  {previewData.commands.map((command, index) => (
                    <div key={command.id} className={`command-item risk-${command.riskLevel}`}>
                      <div className="command-header">
                        <div className="command-number">{index + 1}</div>
                        <div className="command-type">{command.description}</div>
                        <div className="command-risk">
                          <span className={`risk-indicator risk-${command.riskLevel}`}>
                            {command.riskLevel}
                          </span>
                        </div>
                      </div>
                      <div className="command-code">
                        <code>{command.command}</code>
                      </div>
                      {command.warnings && command.warnings.length > 0 && (
                        <div className="command-warnings">
                          {command.warnings.map((warning, wIndex) => (
                            <div key={wIndex} className="warning-item">
                              <AlertCircle size={14} />
                              {warning}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {previewData.riskAnalysis && (
                  <div className="risk-analysis">
                    <h4>Risk Analysis</h4>
                    <div className="risk-summary">
                      <div className="risk-overall">
                        <span className="risk-label">Overall Risk:</span>
                        <span className={`risk-value risk-${previewData.riskAnalysis.overallRisk}`}>
                          {previewData.riskAnalysis.overallRisk}
                        </span>
                      </div>
                      <div className="risk-breakdown">
                        <div className="risk-item">
                          <span className="risk-count risk-low">{previewData.riskAnalysis.riskCounts.low}</span>
                          <span className="risk-label">Low Risk</span>
                        </div>
                        <div className="risk-item">
                          <span className="risk-count risk-medium">{previewData.riskAnalysis.riskCounts.medium}</span>
                          <span className="risk-label">Medium Risk</span>
                        </div>
                        <div className="risk-item">
                          <span className="risk-count risk-high">{previewData.riskAnalysis.riskCounts.high}</span>
                          <span className="risk-label">High Risk</span>
                        </div>
                      </div>
                    </div>
                    {previewData.riskAnalysis.recommendations && previewData.riskAnalysis.recommendations.length > 0 && (
                      <div className="recommendations">
                        <h5>Recommendations:</h5>
                        <ul>
                          {previewData.riskAnalysis.recommendations.map((rec, index) => (
                            <li key={index}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="no-preview">
                <div className="no-preview-icon">
                  <Search size={24} />
                </div>
                <div className="no-preview-text">No preview data available</div>
                <div className="no-preview-subtext">Generate a preview to see commands</div>
              </div>
            )}
          </div>
        ) : (
          <div className="execution-panel">
            <div className="panel-tabs">
              <button
                className={`panel-tab ${rightPanelTab === 'logs' ? 'active' : ''}`}
                onClick={() => { setRightPanelTab('logs'); setSelectedHistoryRecord(null); setHistoricalNodes([]); setHistoricalEdges([]); }}
              >
                <Clipboard size={14} />
                Execution Logs
              </button>
              <button
                className={`panel-tab ${rightPanelTab === 'history' ? 'active' : ''}`}
                onClick={() => { setRightPanelTab('history'); loadExecutionHistory(); }}
              >
                <History size={14} />
                History
              </button>
            </div>

            {rightPanelTab === 'logs' ? (
              <>
                <div className="panel-header">
                  <h3>Execution Logs</h3>
                  <div className="log-controls">
                    <button onClick={clearLogs} className="clear-button">
                      Clear
                    </button>
                    <button onClick={exportLogs} className="export-button">
                      Export
                    </button>
                  </div>
                </div>

                <div className="execution-log">
                  {executionLog.length === 0 ? (
                    <div className="no-logs">
                      <div className="no-logs-icon">
                        <Clipboard size={24} />
                      </div>
                      <div className="no-logs-text">No execution logs yet</div>
                      <div className="no-logs-subtext">Start a workflow execution to see logs here</div>
                    </div>
                  ) : (
                    <div className="log-entries">
                      {executionLog.map((entry, index) => {
                        const isSeparator = entry.type === 'separator';
                        return (
                          <div key={index} className={`log-entry ${entry.type} ${isSeparator ? 'separator' : ''}`}>
                            <div className="log-content">
                              <div className="log-message">{entry.command ? "Command:" : entry.message}</div>
                              {entry.command && (
                                <div className="log-command">
                                  <code>{entry.command}</code>
                                </div>
                              )}
                            </div>
                            <div className="log-time">{entry.timestamp}</div>
                          </div>
                        );
                      })}

                      {/* Processing indicator */}
                      {currentOperation && isExecuting && (
                        <div className="log-entry processing">
                          <div className="log-content">
                            <div className="log-message">
                              Executing {currentOperation.operationType} operation
                              <span className="processing-dots">
                                <span>.</span>
                                <span>.</span>
                                <span>.</span>
                              </span>
                            </div>
                          </div>
                          <div className="log-time">{new Date().toLocaleTimeString()}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : selectedHistoryRecord ? (
              <div className="history-detail-panel">
                <div className="panel-header">
                  <button
                    className="back-to-history-button"
                    onClick={() => { setSelectedHistoryRecord(null); setHistoricalNodes([]); setHistoricalEdges([]); }}
                  >
                    ← Back to History
                  </button>
                  <span className={`history-item-status status-${selectedHistoryRecord.status}`}>
                    {selectedHistoryRecord.status}
                  </span>
                </div>
                <div className="execution-log">
                  {!selectedHistoryRecord.logs ? (
                    <div className="history-loading">Loading logs...</div>
                  ) : selectedHistoryRecord.logs.length === 0 ? (
                    <div className="no-logs">
                      <div className="no-logs-text">No logs recorded</div>
                    </div>
                  ) : (
                    <div className="log-entries">
                      {selectedHistoryRecord.logs.map((entry, index) => {
                        const isSeparator = entry.type === 'separator';
                        const displayTime = entry.timestamp
                          ? new Date(entry.timestamp).toLocaleTimeString()
                          : '';
                        return (
                          <div key={index} className={`log-entry ${entry.type} ${isSeparator ? 'separator' : ''}`}>
                            <div className="log-content">
                              <div className="log-message">{entry.command ? "Command:" : entry.message}</div>
                              {entry.command && (
                                <div className="log-command">
                                  <code>{entry.command}</code>
                                </div>
                              )}
                            </div>
                            <div className="log-time">{displayTime}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="history-panel">
                {isLoadingHistory ? (
                  <div className="history-loading">Loading history...</div>
                ) : executionHistory.length === 0 ? (
                  <div className="no-history">
                    <div className="no-history-icon">
                      <History size={24} />
                    </div>
                    <div className="no-history-text">No execution history yet</div>
                    <div className="no-history-subtext">Run a workflow to see its history here</div>
                  </div>
                ) : (
                  <div className="history-list">
                    {executionHistory.map(record => (
                      <div
                        key={record.id}
                        className="history-item"
                        onClick={() => loadHistoricalExecution(record)}
                      >
                        <div className="history-item-header">
                          <span className={`history-item-status status-${record.status}`}>
                            {record.status}
                          </span>
                          <span className="history-item-time">
                            {record.startTime ? new Date(record.startTime).toLocaleString() : 'Unknown'}
                          </span>
                        </div>
                        {record.durationMs != null && (
                          <div className="history-item-duration">
                            Duration: {(record.durationMs / 1000).toFixed(1)}s
                          </div>
                        )}
                        {record.error && (
                          <div className="history-item-error">{record.error}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {showWorkflowSelector && (
        <div className="modal-overlay">
          <div className="workflow-selector-modal">
            <div className="modal-header">
              <h2>Select Workflow to Run</h2>
              <button 
                className="close-button"
                onClick={() => setShowWorkflowSelector(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <WorkflowManager 
                onLoadWorkflow={handleWorkflowSelected}
                onClose={() => setShowWorkflowSelector(false)}
                showOnlyLoad={true}
              />
            </div>
          </div>
        </div>
      )}

      {showRepositorySelector && (
        <RepositoryConnectModal
          initialPath={repositoryPath}
          onSave={(path) => {
            setRepositoryPath(path);
            setShowRepositorySelector(false);
          }}
          onCancel={() => setShowRepositorySelector(false)}
          showInfo
        />
      )}
    </div>
  );
}

// Branch Node Components with Status Indicators
function ProductionBranchNode({ data, selected }) {
  const tagCount = data.tags?.length || 0;
  
  return (
    <div className={`branch-node production status-${data.status} ${selected ? 'selected' : ''}`}>
      <Handle
        type="target"
        position={Position.Left}
        id="target"
        style={{ background: '#555', opacity: 1, visibility: 'visible' }}
        isConnectable={false}
      />
      <div className="branch-header">
        <div className="branch-type-icon">
          <Factory size={16} />
        </div>
        <span className="branch-type">PROD</span>
        {tagCount > 0 && (
          <span className="tag-inline-badge">
            <Tag size={10} />
            {tagCount}
          </span>
        )}
      </div>
      <div className="branch-name">{data.name}</div>
      {data.isRemote && (
        <div className="remote-indicator">
          <Globe size={12} />
        </div>
      )}
      {data.autoPull && (
        <div className={`auto-pull-indicator ${data.autoPullStatus || ''}`}>
          <svg width="12" height="12" viewBox="0 0 30.727 30.727" fill="currentColor">
            <path d="M29.994,10.183L15.363,24.812L0.733,10.184c-0.977-0.978-0.977-2.561,0-3.536c0.977-0.977,2.559-0.976,3.536,0
              l11.095,11.093L26.461,6.647c0.977-0.976,2.559-0.976,3.535,0C30.971,7.624,30.971,9.206,29.994,10.183z"/>
          </svg>
        </div>
      )}
      {data.autoPush && (
        <div className={`auto-push-indicator ${data.autoPushStatus || ''}`}>
          <svg width="12" height="12" viewBox="0 0 30.727 30.727" fill="currentColor">
            <path d="M0.733,20.544L15.363,5.915L29.994,20.544c0.977,0.978,0.977,2.561,0,3.536c-0.977,0.977-2.559,0.976-3.536,0
              L15.363,13.387L4.269,24.08c-0.977,0.976-2.559,0.976-3.535,0C-0.243,23.105-0.243,21.522,0.733,20.544z"/>
          </svg>
        </div>
      )}
      {data.deleteConfig?.enabled && (
        <div className={`delete-branch-indicator ${data.deleteStatus || ''}`}>
          <Trash2 size={12} />
        </div>
      )}
      <Handle
        type="source"
        position={Position.Right}
        id="source"
        style={{ background: '#555', opacity: 1, visibility: 'visible' }}
        isConnectable={false}
      />
    </div>
  );
}

function FeatureBranchNode({ data, selected }) {
  const tagCount = data.tags?.length || 0;
  
  return (
    <div className={`branch-node feature status-${data.status} ${selected ? 'selected' : ''}`}>
      <Handle
        type="target"
        position={Position.Left}
        id="target"
        style={{ background: '#555', opacity: 1, visibility: 'visible' }}
        isConnectable={false}
      />
      <div className="branch-header">
        <div className="branch-type-icon">
          <Wrench size={16} />
        </div>
        <span className="branch-type">FEATURE</span>
        {tagCount > 0 && (
          <span className="tag-inline-badge">
            <Tag size={10} />
            {tagCount}
          </span>
        )}
      </div>
      <div className="branch-name">{data.name}</div>
      {data.isRemote && (
        <div className="remote-indicator">
          <Globe size={12} />
        </div>
      )}
      {data.autoPull && (
        <div className={`auto-pull-indicator ${data.autoPullStatus || ''}`}>
          <svg width="12" height="12" viewBox="0 0 30.727 30.727" fill="currentColor">
            <path d="M29.994,10.183L15.363,24.812L0.733,10.184c-0.977-0.978-0.977-2.561,0-3.536c0.977-0.977,2.559-0.976,3.536,0
              l11.095,11.093L26.461,6.647c0.977-0.976,2.559-0.976,3.535,0C30.971,7.624,30.971,9.206,29.994,10.183z"/>
          </svg>
        </div>
      )}
      {data.autoPush && (
        <div className={`auto-push-indicator ${data.autoPushStatus || ''}`}>
          <svg width="12" height="12" viewBox="0 0 30.727 30.727" fill="currentColor">
            <path d="M0.733,20.544L15.363,5.915L29.994,20.544c0.977,0.978,0.977,2.561,0,3.536c-0.977,0.977-2.559,0.976-3.536,0
              L15.363,13.387L4.269,24.08c-0.977,0.976-2.559,0.976-3.535,0C-0.243,23.105-0.243,21.522,0.733,20.544z"/>
          </svg>
        </div>
      )}
      {data.deleteConfig?.enabled && (
        <div className={`delete-branch-indicator ${data.deleteStatus || ''}`}>
          <Trash2 size={12} />
        </div>
      )}
      <Handle
        type="source"
        position={Position.Right}
        id="source"
        style={{ background: '#555', opacity: 1, visibility: 'visible' }}
        isConnectable={false}
      />
    </div>
  );
}

function ReleaseBranchNode({ data, selected }) {
  const tagCount = data.tags?.length || 0;
  
  return (
    <div className={`branch-node release status-${data.status} ${selected ? 'selected' : ''}`}>
      <Handle
        type="target"
        position={Position.Left}
        id="target"
        style={{ background: '#555', opacity: 1, visibility: 'visible' }}
        isConnectable={false}
      />
      <div className="branch-header">
        <div className="branch-type-icon">
          <Rocket size={16} />
        </div>
        <span className="branch-type">RELEASE</span>
        {tagCount > 0 && (
          <span className="tag-inline-badge">
            <Tag size={10} />
            {tagCount}
          </span>
        )}
      </div>
      <div className="branch-name">{data.name}</div>
      {data.isRemote && (
        <div className="remote-indicator">
          <Globe size={12} />
        </div>
      )}
      {data.autoPull && (
        <div className={`auto-pull-indicator ${data.autoPullStatus || ''}`}>
          <svg width="12" height="12" viewBox="0 0 30.727 30.727" fill="currentColor">
            <path d="M29.994,10.183L15.363,24.812L0.733,10.184c-0.977-0.978-0.977-2.561,0-3.536c0.977-0.977,2.559-0.976,3.536,0
              l11.095,11.093L26.461,6.647c0.977-0.976,2.559-0.976,3.535,0C30.971,7.624,30.971,9.206,29.994,10.183z"/>
          </svg>
        </div>
      )}
      {data.autoPush && (
        <div className={`auto-push-indicator ${data.autoPushStatus || ''}`}>
          <svg width="12" height="12" viewBox="0 0 30.727 30.727" fill="currentColor">
            <path d="M0.733,20.544L15.363,5.915L29.994,20.544c0.977,0.978,0.977,2.561,0,3.536c-0.977,0.977-2.559,0.976-3.536,0
              L15.363,13.387L4.269,24.08c-0.977,0.976-2.559,0.976-3.535,0C-0.243,23.105-0.243,21.522,0.733,20.544z"/>
          </svg>
        </div>
      )}
      {data.deleteConfig?.enabled && (
        <div className={`delete-branch-indicator ${data.deleteStatus || ''}`}>
          <Trash2 size={12} />
        </div>
      )}
      <Handle
        type="source"
        position={Position.Right}
        id="source"
        style={{ background: '#555', opacity: 1, visibility: 'visible' }}
        isConnectable={false}
      />
    </div>
  );
}

function HotfixBranchNode({ data, selected }) {
  const tagCount = data.tags?.length || 0;
  
  return (
    <div className={`branch-node hotfix status-${data.status} ${selected ? 'selected' : ''}`}>
      <Handle
        type="target"
        position={Position.Left}
        id="target"
        style={{ background: '#555', opacity: 1, visibility: 'visible' }}
        isConnectable={false}
      />
      <div className="branch-header">
        <div className="branch-type-icon">
          <AlertTriangle size={16} />
        </div>
        <span className="branch-type">HOTFIX</span>
        {tagCount > 0 && (
          <span className="tag-inline-badge">
            <Tag size={10} />
            {tagCount}
          </span>
        )}
      </div>
      <div className="branch-name">{data.name}</div>
      {data.isRemote && (
        <div className="remote-indicator">
          <Globe size={12} />
        </div>
      )}
      {data.autoPull && (
        <div className={`auto-pull-indicator ${data.autoPullStatus || ''}`}>
          <svg width="12" height="12" viewBox="0 0 30.727 30.727" fill="currentColor">
            <path d="M29.994,10.183L15.363,24.812L0.733,10.184c-0.977-0.978-0.977-2.561,0-3.536c0.977-0.977,2.559-0.976,3.536,0
              l11.095,11.093L26.461,6.647c0.977-0.976,2.559-0.976,3.535,0C30.971,7.624,30.971,9.206,29.994,10.183z"/>
          </svg>
        </div>
      )}
      {data.autoPush && (
        <div className={`auto-push-indicator ${data.autoPushStatus || ''}`}>
          <svg width="12" height="12" viewBox="0 0 30.727 30.727" fill="currentColor">
            <path d="M0.733,20.544L15.363,5.915L29.994,20.544c0.977,0.978,0.977,2.561,0,3.536c-0.977,0.977-2.559,0.976-3.536,0
              L15.363,13.387L4.269,24.08c-0.977,0.976-2.559,0.976-3.535,0C-0.243,23.105-0.243,21.522,0.733,20.544z"/>
          </svg>
        </div>
      )}
      {data.deleteConfig?.enabled && (
        <div className={`delete-branch-indicator ${data.deleteStatus || ''}`}>
          <Trash2 size={12} />
        </div>
      )}
      <Handle
        type="source"
        position={Position.Right}
        id="source"
        style={{ background: '#555', opacity: 1, visibility: 'visible' }}
        isConnectable={false}
      />
    </div>
  );
}

function DevelopBranchNode({ data, selected }) {
  const tagCount = data.tags?.length || 0;
  
  return (
    <div className={`branch-node develop status-${data.status} ${selected ? 'selected' : ''}`}>
      <Handle
        type="target"
        position={Position.Left}
        id="target"
        style={{ background: '#555', opacity: 1, visibility: 'visible' }}
        isConnectable={false}
      />
      <div className="branch-header">
        <div className="branch-type-icon">
          <Settings size={16} />
        </div>
        <span className="branch-type">DEVELOP</span>
        {tagCount > 0 && (
          <span className="tag-inline-badge">
            <Tag size={10} />
            {tagCount}
          </span>
        )}
      </div>
      <div className="branch-name">{data.name}</div>
      {data.isRemote && (
        <div className="remote-indicator">
          <Globe size={12} />
        </div>
      )}
      {data.autoPull && (
        <div className={`auto-pull-indicator ${data.autoPullStatus || ''}`}>
          <svg width="12" height="12" viewBox="0 0 30.727 30.727" fill="currentColor">
            <path d="M29.994,10.183L15.363,24.812L0.733,10.184c-0.977-0.978-0.977-2.561,0-3.536c0.977-0.977,2.559-0.976,3.536,0
              l11.095,11.093L26.461,6.647c0.977-0.976,2.559-0.976,3.535,0C30.971,7.624,30.971,9.206,29.994,10.183z"/>
          </svg>
        </div>
      )}
      {data.autoPush && (
        <div className={`auto-push-indicator ${data.autoPushStatus || ''}`}>
          <svg width="12" height="12" viewBox="0 0 30.727 30.727" fill="currentColor">
            <path d="M0.733,20.544L15.363,5.915L29.994,20.544c0.977,0.978,0.977,2.561,0,3.536c-0.977,0.977-2.559,0.976-3.536,0
              L15.363,13.387L4.269,24.08c-0.977,0.976-2.559,0.976-3.535,0C-0.243,23.105-0.243,21.522,0.733,20.544z"/>
          </svg>
        </div>
      )}
      {data.deleteConfig?.enabled && (
        <div className={`delete-branch-indicator ${data.deleteStatus || ''}`}>
          <Trash2 size={12} />
        </div>
      )}
      <Handle
        type="source"
        position={Position.Right}
        id="source"
        style={{ background: '#555', opacity: 1, visibility: 'visible' }}
        isConnectable={false}
      />
    </div>
  );
}

function StagingBranchNode({ data, selected }) {
  const tagCount = data.tags?.length || 0;
  
  return (
    <div className={`branch-node staging status-${data.status} ${selected ? 'selected' : ''}`}>
      <Handle
        type="target"
        position={Position.Left}
        id="target"
        style={{ background: '#555', opacity: 1, visibility: 'visible' }}
        isConnectable={false}
      />
      <div className="branch-header">
        <div className="branch-type-icon">
          <TestTube size={16} />
        </div>
        <span className="branch-type">STAGING</span>
        {tagCount > 0 && (
          <span className="tag-inline-badge">
            <Tag size={10} />
            {tagCount}
          </span>
        )}
      </div>
      <div className="branch-name">{data.name}</div>
      {data.isRemote && (
        <div className="remote-indicator">
          <Globe size={12} />
        </div>
      )}
      {data.autoPull && (
        <div className={`auto-pull-indicator ${data.autoPullStatus || ''}`}>
          <svg width="12" height="12" viewBox="0 0 30.727 30.727" fill="currentColor">
            <path d="M29.994,10.183L15.363,24.812L0.733,10.184c-0.977-0.978-0.977-2.561,0-3.536c0.977-0.977,2.559-0.976,3.536,0
              l11.095,11.093L26.461,6.647c0.977-0.976,2.559-0.976,3.535,0C30.971,7.624,30.971,9.206,29.994,10.183z"/>
          </svg>
        </div>
      )}
      {data.autoPush && (
        <div className={`auto-push-indicator ${data.autoPushStatus || ''}`}>
          <svg width="12" height="12" viewBox="0 0 30.727 30.727" fill="currentColor">
            <path d="M0.733,20.544L15.363,5.915L29.994,20.544c0.977,0.978,0.977,2.561,0,3.536c-0.977,0.977-2.559,0.976-3.536,0
              L15.363,13.387L4.269,24.08c-0.977,0.976-2.559,0.976-3.535,0C-0.243,23.105-0.243,21.522,0.733,20.544z"/>
          </svg>
        </div>
      )}
      {data.deleteConfig?.enabled && (
        <div className={`delete-branch-indicator ${data.deleteStatus || ''}`}>
          <Trash2 size={12} />
        </div>
      )}
      <Handle
        type="source"
        position={Position.Right}
        id="source"
        style={{ background: '#555', opacity: 1, visibility: 'visible' }}
        isConnectable={false}
      />
    </div>
  );
}

function IntegrationBranchNode({ data, selected }) {
  const tagCount = data.tags?.length || 0;
  
  return (
    <div className={`branch-node integration status-${data.status} ${selected ? 'selected' : ''}`}>
      <Handle
        type="target"
        position={Position.Left}
        id="target"
        style={{ background: '#555', opacity: 1, visibility: 'visible' }}
        isConnectable={false}
      />
      <div className="branch-header">
        <div className="branch-type-icon">
          <Link size={16} />
        </div>
        <span className="branch-type">INTEGRATION</span>
        {tagCount > 0 && (
          <span className="tag-inline-badge">
            <Tag size={10} />
            {tagCount}
          </span>
        )}
      </div>
      <div className="branch-name">{data.name}</div>
      {data.isRemote && (
        <div className="remote-indicator">
          <Globe size={12} />
        </div>
      )}
      {data.autoPull && (
        <div className={`auto-pull-indicator ${data.autoPullStatus || ''}`}>
          <svg width="12" height="12" viewBox="0 0 30.727 30.727" fill="currentColor">
            <path d="M29.994,10.183L15.363,24.812L0.733,10.184c-0.977-0.978-0.977-2.561,0-3.536c0.977-0.977,2.559-0.976,3.536,0
              l11.095,11.093L26.461,6.647c0.977-0.976,2.559-0.976,3.535,0C30.971,7.624,30.971,9.206,29.994,10.183z"/>
          </svg>
        </div>
      )}
      {data.autoPush && (
        <div className={`auto-push-indicator ${data.autoPushStatus || ''}`}>
          <svg width="12" height="12" viewBox="0 0 30.727 30.727" fill="currentColor">
            <path d="M0.733,20.544L15.363,5.915L29.994,20.544c0.977,0.978,0.977,2.561,0,3.536c-0.977,0.977-2.559,0.976-3.536,0
              L15.363,13.387L4.269,24.08c-0.977,0.976-2.559,0.976-3.535,0C-0.243,23.105-0.243,21.522,0.733,20.544z"/>
          </svg>
        </div>
      )}
      {data.deleteConfig?.enabled && (
        <div className={`delete-branch-indicator ${data.deleteStatus || ''}`}>
          <Trash2 size={12} />
        </div>
      )}
      <Handle
        type="source"
        position={Position.Right}
        id="source"
        style={{ background: '#555', opacity: 1, visibility: 'visible' }}
        isConnectable={false}
      />
    </div>
  );
}

// Branch node types (defined after all components)
const branchNodeTypes = {
  production: ProductionBranchNode,
  feature: FeatureBranchNode,
  release: ReleaseBranchNode,
  hotfix: HotfixBranchNode,
  develop: DevelopBranchNode,
  staging: StagingBranchNode,
  integration: IntegrationBranchNode,
};

// Arrow Markers Component - Using ReactFlow's marker system
function ArrowMarkers() {
  return (
    <svg style={{ position: 'absolute', top: 0, left: 0, width: 0, height: 0, zIndex: -1 }}>
      <defs>
        {/* Arrow - Normal */}
        <marker
          id="arrow-normal"
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
          viewBox="0 0 10 10"
        >
          <path d="M2,1 L6,3 L2,5 z" fill="#9ca3af" stroke="#9ca3af" strokeWidth="0.5" />
        </marker>
        
        {/* Arrow - Selected */}
        <marker
          id="arrow-selected"
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
          viewBox="0 0 10 10"
        >
          <path d="M2,1 L6,3 L2,5 z" fill="#3b82f6" stroke="#3b82f6" strokeWidth="0.5" />
        </marker>
        
        {/* Arrow - Running */}
        <marker
          id="arrow-running"
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
          viewBox="0 0 10 10"
        >
          <path d="M2,1 L6,3 L2,5 z" fill="#17a2b8" stroke="#17a2b8" strokeWidth="0.5" />
        </marker>
        
        {/* Arrow - Success */}
        <marker
          id="arrow-success"
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
          viewBox="0 0 10 10"
        >
          <path d="M2,1 L6,3 L2,5 z" fill="#28a745" stroke="#28a745" strokeWidth="0.5" />
        </marker>
        
        {/* Arrow - Failed */}
        <marker
          id="arrow-failed"
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
          viewBox="0 0 10 10"
        >
          <path d="M2,1 L6,3 L2,5 z" fill="#dc3545" stroke="#dc3545" strokeWidth="0.5" />
        </marker>
      </defs>
    </svg>
  );
}

// Operation Edge Component with Status
function OperationEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, selected }) {
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Get operation display text and styling based on type and params
  const getOperationDisplay = (operationType, params = {}) => {
    switch (operationType) {
      case 'checkout':
        let checkoutText = 'checkout';
        let checkoutColor = '#6b7280';
        let checkoutBgColor = '#f9fafb';
        
        if (params.new) {
          checkoutText = 'checkout -b';
          checkoutColor = '#3b82f6';
          checkoutBgColor = '#eff6ff';
          
          if (params.reset) {
            checkoutText = 'checkout -B';
          }
        } else if (params.reset) {
          // Only reset flag without new
          checkoutText = 'checkout -B';
          checkoutColor = '#3b82f6';
          checkoutBgColor = '#eff6ff';
        }
        
        if (params.force) {
          if (checkoutText === 'checkout') {
            checkoutText = 'checkout -f';
          } else if (checkoutText.includes('-b')) {
            checkoutText = checkoutText.replace('-b', '-f -b');
          } else if (checkoutText.includes('-B')) {
            checkoutText = checkoutText.replace('-B', '-f -B');
          }
        }
        
        return { text: checkoutText, color: checkoutColor, bgColor: checkoutBgColor };
      case 'merge':
        let mergeText = 'merge';
        let mergeColor = '#10b981';
        let mergeBgColor = '#ecfdf5';
        
        if (params.strategy === 'squash') {
          mergeText = 'merge --squash';
        } else if (params.strategy === 'standard') {
          if (params.ffOption === 'no-ff') {
            mergeText = 'merge --no-ff';
          } else if (params.ffOption === 'ff-only') {
            mergeText = 'merge --ff-only';
          }
          // 'auto' shows just 'merge'
        }
        
        return { text: mergeText, color: mergeColor, bgColor: mergeBgColor };
      case 'rebase':
        return { text: 'rebase', color: '#f59e0b', bgColor: '#fffbeb' };
      case 'push':
        let pushText = 'push';
        let pushColor = '#8b5cf6';
        let pushBgColor = '#f3e8ff';
        
        // Build command parts
        const parts = [];
        
        // Add upstream flag first if needed
        if (params.upstream) {
          parts.push('-u');
        }
        
        // Add force options
        if (params.forceType === 'forceWithLease') {
          parts.push('--force-with-lease');
        } else if (params.forceType === 'force') {
          parts.push('--force');
        }
        
        // Add remote name
        if (params.remote) {
          parts.push(params.remote);
        }
        
        // Build final command
        if (parts.length > 0) {
          pushText = `push ${parts.join(' ')}`;
        }
        
        return { text: pushText, color: pushColor, bgColor: pushBgColor };
      case 'pull':
        return { text: 'pull', color: '#06b6d4', bgColor: '#ecfeff' };
      case 'delete-branch':
        return { text: 'delete', color: '#ef4444', bgColor: '#fef2f2' };
      case 'tag':
        return { text: 'tag', color: '#f97316', bgColor: '#fff7ed' };
      default:
        return { text: operationType, color: '#6b7280', bgColor: '#f9fafb' };
    }
  };

  // Get arrow marker ID based on status and selection state
  const getArrowMarkerId = (status, selected) => {
    if (selected) return 'arrow-selected';
    
    switch (status) {
      case 'running':
        return 'arrow-running';
      case 'success':
        return 'arrow-success';
      case 'failed':
        return 'arrow-failed';
      default: // pending
        return 'arrow-normal';
    }
  };

  const operationDisplay = getOperationDisplay(data.operationType, data.params);
  const arrowMarkerId = getArrowMarkerId(data.status, selected);

  // Get status-based styling
  const getStatusStyling = (status) => {
    switch (status) {
      case 'running':
        return {
          stroke: '#17a2b8',
          strokeWidth: 3,
          strokeDasharray: 'none'
        };
      case 'success':
        return {
          stroke: '#28a745',
          strokeWidth: 3,
          strokeDasharray: 'none'
        };
      case 'failed':
        return {
          stroke: '#dc3545',
          strokeWidth: 3,
          strokeDasharray: 'none'
        };
      default: // pending
        return {
          stroke: selected ? '#667eea' : '#9ca3af',
          strokeWidth: selected ? 3 : 2,
          strokeDasharray: data.operationType === 'checkout' && (data.params?.new || data.params?.reset) ? '5,5' : 'none'
        };
    }
  };

  const getStatusLabelStyling = (status) => {
    switch (status) {
      case 'running':
        return {
          background: '#f7fcfd',
          border: '1px solid #17a2b8',
          color: '#0c5460'
        };
      case 'success':
        return {
          background: '#f8fff9',
          border: '1px solid #28a745',
          color: '#155724'
        };
      case 'failed':
        return {
          background: '#fef2f2',
          border: '1px solid #dc3545',
          color: '#721c24'
        };
      default: // pending
        return {
          background: operationDisplay.bgColor,
          border: `1px solid ${operationDisplay.color}`,
          color: operationDisplay.color
        };
    }
  };

  const statusStyling = getStatusStyling(data.status);
  const labelStyling = getStatusLabelStyling(data.status);

  return (
    <>
      <BaseEdge 
        id={id} 
        path={edgePath} 
        markerEnd={`url(#${arrowMarkerId})`}
        style={statusStyling}
        className={`operation-edge operation-edge-${data.status}`}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            padding: '6px 12px',
            borderRadius: '8px',
            fontSize: '11px',
            fontWeight: 600,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            whiteSpace: 'nowrap',
            letterSpacing: '0.3px',
            ...labelStyling
          }}
          className={`nodrag nopan operation-label operation-label-${data.status}`}
        >
          {operationDisplay.text}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

export default WorkflowRunner;